import React, { useState, useEffect, useMemo } from 'react';
import { Briefcase, Loader2, Info, Calendar, Clock, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

const DAY_DEFS = [
    { key: 'mondayShiftGroup', label: 'Pzt' },
    { key: 'tuesdayShiftGroup', label: 'Sal' },
    { key: 'wednesdayShiftGroup', label: 'Çar' },
    { key: 'thursdayShiftGroup', label: 'Per' },
    { key: 'fridayShiftGroup', label: 'Cum' },
    { key: 'saturdayShiftGroup', label: 'Cmt' },
    { key: 'sundayShiftGroup', label: 'Paz' },
];

export default function WorkingRegimeForm({
    workingRegime,
    workPeriods = [],
    onSave,
    onCancel,
    isNew,
    submitting
}) {
    const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        isActive: true,
        defaultWorkPeriodId: '',
        syncExistingEmployeesSchedule: false,
        preserveCustomEmployeeSchedules: true,
        scheduleEffectiveDate: todayStr,
    });
    const [formError, setFormError] = useState(null);

    useEffect(() => {
        if (workingRegime) {
            const hasPeriodChangedDefault = false;
            setFormData({
                id: workingRegime.id,
                name: workingRegime.name || '',
                description: workingRegime.description || '',
                isActive: workingRegime.isActive ?? true,
                defaultWorkPeriodId: workingRegime.defaultWorkPeriodId ? String(workingRegime.defaultWorkPeriodId) : '',
                syncExistingEmployeesSchedule: hasPeriodChangedDefault,
                preserveCustomEmployeeSchedules: true,
                scheduleEffectiveDate: todayStr,
            });
        } else {
            setFormData({
                name: '',
                description: '',
                isActive: true,
                defaultWorkPeriodId: '',
                syncExistingEmployeesSchedule: false,
                preserveCustomEmployeeSchedules: true,
                scheduleEffectiveDate: todayStr,
            });
        }
        setFormError(null);
    }, [workingRegime, isNew, todayStr]);

    const selectedPeriodObj = useMemo(() => {
        if (!formData.defaultWorkPeriodId) return null;
        return workPeriods.find(p => String(p.id) === String(formData.defaultWorkPeriodId)) || null;
    }, [formData.defaultWorkPeriodId, workPeriods]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const next = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
            // Düzenleme modunda periyot değiştirildiyse ve aktif personel varsa varsayılan olarak senkronizasyonu öner
            if (name === 'defaultWorkPeriodId' && !isNew && (workingRegime?.activeEmployeeCount || 0) > 0) {
                if (value && String(value) !== String(workingRegime?.defaultWorkPeriodId || '')) {
                    next.syncExistingEmployeesSchedule = true;
                }
            }
            return next;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError(null);

        if (!formData.name.trim()) {
            setFormError("Çalışma rejimi adı boş bırakılamaz.");
            return;
        }

        onSave({
            ...formData,
            name: formData.name.trim(),
            description: formData.description ? formData.description.trim() : null,
            defaultWorkPeriodId: formData.defaultWorkPeriodId ? parseInt(formData.defaultWorkPeriodId, 10) : null,
            scheduleEffectiveDate: formData.syncExistingEmployeesSchedule && formData.scheduleEffectiveDate
                ? formData.scheduleEffectiveDate
                : null
        });
    };

    const activeEmpCount = workingRegime?.activeEmployeeCount || 0;

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
                <div className="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 p-3 rounded-lg text-sm border border-red-200 dark:border-red-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span>{formError}</span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
                        <Briefcase className="w-3.5 h-3.5 text-indigo-500" /> Çalışma Rejimi Adı *
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        maxLength={100}
                        placeholder="Örn: Beyaz Yaka, Mavi Yaka, 3 Vardiya..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        disabled={submitting}
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-indigo-500" /> Varsayılan Çalışma Periyodu (Otomatik Vardiya)
                    </label>
                    <select
                        name="defaultWorkPeriodId"
                        value={formData.defaultWorkPeriodId}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        disabled={submitting}
                    >
                        <option value="">-- Otomatik Vardiya Ataması Yok (Manuel) --</option>
                        {workPeriods.map(wp => (
                            <option key={wp.id} value={wp.id}>
                                {wp.name} {wp.hasDynamicDayOff ? `(Dinamik ${wp.dynamicDayOffCount || 1} Gün Tatil)` : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Seçilen Çalışma Periyodunun 7 Günlük Önizlemesi */}
            {selectedPeriodObj ? (
                <div className="p-3 rounded-lg bg-indigo-50/70 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/60 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                            Otomatik Vardiya Şablonu: <span className="underline">{selectedPeriodObj.name}</span>
                        </span>
                        {selectedPeriodObj.hasDynamicDayOff && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 font-medium">
                                Dinamik Hafta Tatili ({selectedPeriodObj.dynamicDayOffCount || 1} Gün)
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-7 gap-1.5">
                        {DAY_DEFS.map(day => {
                            const sg = selectedPeriodObj[day.key];
                            const isWork = !!sg;
                            return (
                                <div
                                    key={day.key}
                                    className={`p-1.5 rounded text-center border ${
                                        isWork
                                            ? 'bg-white dark:bg-gray-800 border-indigo-200 dark:border-indigo-700/60 text-indigo-700 dark:text-indigo-300'
                                            : 'bg-gray-100 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                                    }`}
                                >
                                    <div className="text-[10px] font-bold uppercase">{day.label}</div>
                                    <div className="text-[10px] truncate mt-0.5 font-medium" title={sg ? sg.name : 'Tatil'}>
                                        {sg ? sg.name : 'Tatil'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-indigo-700/90 dark:text-indigo-300/80">
                        Bu çalışma rejimine eklenen yeni personellere <strong>İşe Giriş Tarihinden</strong> itibaren bu periyot otomatik olarak atanacaktır.
                    </p>
                </div>
            ) : (
                <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                    Varsayılan bir çalışma periyodu seçilmediğinde, bu rejimdeki personellerin vardiyaları <strong>Personel Vardiya Atama</strong> ekranından manuel atanır.
                </div>
            )}

            {/* Düzenleme modunda mevcut personellere senkronizasyon seçeneği */}
            {!isNew && formData.defaultWorkPeriodId && activeEmpCount > 0 && (
                <div className="p-3.5 rounded-lg bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/70 space-y-2.5">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                            type="checkbox"
                            name="syncExistingEmployeesSchedule"
                            checked={formData.syncExistingEmployeesSchedule}
                            onChange={handleChange}
                            className="mt-0.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            disabled={submitting}
                        />
                        <div className="text-xs">
                            <span className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                                <RefreshCw className="w-3.5 h-3.5" />
                                Bu rejimdeki mevcut {activeEmpCount} aktif personelin vardiyasını da bu periyoda güncelle
                            </span>
                            <p className="text-amber-700 dark:text-amber-300/80 mt-0.5">
                                Hiç vardiyası olmayan personellere <strong>İşe Giriş Tarihinden</strong>, mevcut vardiyası olanlara ise seçeceğiniz <strong>Geçerlilik Tarihinden</strong> itibaren tarih kırılımlı (eski vardiyayı bir gün önce kapatarak) atama yapılır.
                            </p>
                        </div>
                    </label>

                    {formData.syncExistingEmployeesSchedule && (
                        <div className="pl-6 pt-2 border-t border-amber-200/70 dark:border-amber-800/50 grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                            <div>
                                <label className="block text-[11px] font-semibold text-amber-900 dark:text-amber-200 mb-1 flex items-center gap-1">
                                    <Calendar className="w-3.5 h-3.5" /> Geçerlilik Başlangıç Tarihi (Vardiyası Olanlar İçin):
                                </label>
                                <input
                                    type="date"
                                    name="scheduleEffectiveDate"
                                    value={formData.scheduleEffectiveDate}
                                    onChange={handleChange}
                                    className="w-full px-2.5 py-1.5 text-xs border border-amber-300 dark:border-amber-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                    disabled={submitting}
                                />
                            </div>
                            <div>
                                <label className="flex items-start gap-2 cursor-pointer text-xs text-amber-900 dark:text-amber-200">
                                    <input
                                        type="checkbox"
                                        name="preserveCustomEmployeeSchedules"
                                        checked={formData.preserveCustomEmployeeSchedules}
                                        onChange={handleChange}
                                        className="mt-0.5 h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded"
                                        disabled={submitting}
                                    />
                                    <span>
                                        <strong>Özel/Geçici vardiyalıları koru</strong>
                                        <span className="block text-[10px] text-amber-700 dark:text-amber-300/80">
                                            İşaretlenirse, farklı/geçici vardiyaya atanmış personellerin özel vardiyası bozulmaz.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5 text-indigo-500" /> Açıklama
                </label>
                <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="2"
                    maxLength={500}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={submitting}
                    placeholder="Bu çalışma rejiminin kapsamı hakkında kısa açıklama..."
                ></textarea>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <label htmlFor="isActive" className="flex items-center cursor-pointer select-none">
                    <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        disabled={submitting}
                    />
                    <span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-200">
                        Aktif (Yeni personel kayıtlarında seçilebilir)
                    </span>
                </label>

                <div className="flex space-x-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        disabled={submitting}
                    >
                        İptal
                    </button>
                    <button
                        type="submit"
                        className="inline-flex justify-center items-center px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                Kaydediliyor...
                            </>
                        ) : (
                            "Kaydet"
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
}
